import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CheckBox,
  FormField,
  Heading,
  Layer,
  Meter,
  Spinner,
  Text,
  TextInput,
} from "grommet";
import { Add, Checkmark, Edit, Refresh, Trash } from "grommet-icons";
import { presentAlertPrompt } from "@shared/Alert";
import { presentLoading, dismissLoading } from "@shared/Loading";
import {
  presentToastSuccess,
  presentToastErrorContent,
  presentToastWarning,
} from "@shared/Toast";
import passcodeService from "@services/passcode.service";
import { useFetchDoors } from "./redux/hooks";
import { useTranslation } from "react-i18next";
import { useFetchHotel } from "./redux/hooks";
import {
  isMasterCodeDoor,
  isPendingMasterCode,
  masterCodeResults,
} from "../../common/masterCodes";

export default function MasterPasscodes() {
  const { t } = useTranslation();
  const { doors } = useFetchDoors();
  const { hotel } = useFetchHotel();
  const hotelId = useRef(hotel?._id);
  hotelId.current = hotel?._id;

  const removalWaiter = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const [masterCodes, setMasterCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form state
  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");
  const [formDoorIds, setFormDoorIds] = useState([]);
  const [allDoors, setAllDoors] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total } while batching across many doors

  const BATCH_SIZE = 5;

  const eligibleDoors = (doors || []).filter((door) =>
    isMasterCodeDoor(hotel, door),
  );
  const pending = masterCodes.some((code) =>
    code.accesses?.some(isPendingMasterCode),
  );

  const loadMasterCodes = useCallback(async (background = false) => {
    const scope = hotelId.current;
    if (!background) setLoading(true);
    try {
      const res = await passcodeService.getMasterPasscodes();
      if (mounted.current && scope === hotelId.current) {
        setMasterCodes(res.data || []);
        removalWaiter.current?.check(res.data || []);
      }
    } catch (err) {
      if (scope === hotelId.current) presentToastErrorContent(err);
    } finally {
      if (scope === hotelId.current && !background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setMasterCodes([]);
    setShowAddModal(false);
    loadMasterCodes();
    return () => removalWaiter.current?.finish(false);
  }, [hotel?._id, loadMasterCodes]);

  useEffect(() => {
    if (!hotel?._id) return undefined;
    let busy = false;
    const refresh = async () => {
      if (busy) return;
      busy = true;
      try {
        await loadMasterCodes(true);
      } finally {
        busy = false;
      }
    };
    const changed = ({ detail }) => {
      if (String(detail?.hotelId) === String(hotel._id)) refresh();
    };
    window.addEventListener("loxe:schlage-passcode", changed);
    const timer = pending ? setInterval(refresh, 10000) : undefined;
    return () => {
      clearInterval(timer);
      window.removeEventListener("loxe:schlage-passcode", changed);
    };
  }, [hotel?._id, pending, loadMasterCodes]);

  const handleSync = async () => {
    presentLoading();
    try {
      const res = await passcodeService.syncMasterPasscodes();
      presentToastSuccess(res.data?.message || t("passcodes.syncDone"));
      await loadMasterCodes();
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      dismissLoading();
    }
  };

  const handleDelete = useCallback(
    (master) => {
      presentAlertPrompt({
        title: t("passcodes.deleteConfirmTitle"),
        message: t("passcodes.deleteConfirmMessage", {
          name: master.name,
          code: master.code,
        }),
        buttonOKText: t("common.delete"),
        buttonOKColor: "status-critical",
        onOK: async () => {
          presentLoading();
          try {
            const res = await passcodeService.deleteMasterPasscode(master.code);
            if (res.data?.partially) {
              presentToastWarning(res.data.message);
            } else if (masterCodeResults(res.data).some(isPendingMasterCode)) {
              presentToastWarning(t("accessList.schlage.deleting"));
            } else {
              presentToastSuccess(res.data?.message || t("passcodes.deleted"));
            }
            await loadMasterCodes();
          } catch (err) {
            presentToastErrorContent(err);
          } finally {
            dismissLoading();
          }
        },
      });
    },
    [loadMasterCodes, t],
  );

  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormCode("");
    setFormName("");
    setFormDoorIds([]);
    setAllDoors(true);
    setProgress(null);
    setShowAddModal(true);
  };

  const handleOpenEdit = useCallback(
    (master) => {
      // Resolve which door IDs currently hold this master code
      const doorsWithCode = eligibleDoors.filter((d) =>
        master.accesses
          ? master.accesses.some(
              (access) => String(access.doorId) === String(d._id),
            )
          : (master.doors || []).includes(d.name),
      );
      const doorIds = doorsWithCode.map((d) => d._id);
      const appliesToAll = doorsWithCode.length === eligibleDoors.length;

      setIsEditMode(true);
      setFormCode(master.code);
      setFormName(master.name);
      setFormDoorIds(doorIds);
      setOriginalDoorIds(doorIds);
      setAllDoors(appliesToAll);
      setProgress(null);
      setShowAddModal(true);
    },
    [eligibleDoors],
  );

  const toggleDoor = (doorId) => {
    setFormDoorIds((prev) =>
      prev.includes(doorId)
        ? prev.filter((id) => id !== doorId)
        : [...prev, doorId],
    );
  };

  const [originalDoorIds, setOriginalDoorIds] = useState([]);

  // Continue this exact save only after the removed records disappear on a
  // server re-read. Socket refreshes and polling both feed the same waiter.
  const waitForRemoval = (code, removed) => new Promise((resolve) => {
    const finish = (confirmed) => {
      clearInterval(poll);
      clearTimeout(timeout);
      removalWaiter.current = null;
      resolve(confirmed);
    };
    const poll = setInterval(() => loadMasterCodes(true), 10000);
    const timeout = setTimeout(() => finish(false), 120000);
    removalWaiter.current = {
      finish,
      check: (records) => {
        const remaining = records.filter(record => record.code === code)
          .flatMap(record => record.accesses || []);
        // A legacy response without per-door state cannot confirm deletion.
        if (records.some(record => record.code === code && !record.accesses)) return;
        const targets = remaining.filter(access => removed.includes(access.doorId));
        if (!targets.length) finish(true);
        else if (targets.some(access => access.commandError && !access.pendingOperation)) finish(false);
      },
    };
    loadMasterCodes(true);
  });

  const handleSave = async () => {
    if (!formCode || !formName) return;
    setSubmitting(true);
    const scope = hotelId.current;
    const doorIds = allDoors
      ? eligibleDoors.map((door) => door._id)
      : formDoorIds;
    const doorsToProcess = doorIds;
    if (isEditMode) {
      const removed = originalDoorIds.filter(id => !doorIds.includes(id));
      if (removed.length) {
        try {
          const result = await passcodeService.deleteMasterPasscode(formCode, removed);
          if (!mounted.current || scope !== hotelId.current) { setSubmitting(false); return; }
          if (result.data?.partially) {
            presentToastWarning(t("passcodes.partiallyApplied"));
            await loadMasterCodes();
            setSubmitting(false);
            return;
          }
          if (masterCodeResults(result.data).some(isPendingMasterCode)) {
            const confirmed = await waitForRemoval(formCode, removed);
            if (!confirmed || !mounted.current || scope !== hotelId.current) {
              if (mounted.current && scope === hotelId.current) presentToastWarning(t("passcodes.partiallyApplied"));
              setSubmitting(false);
              return;
            }
          }
          setOriginalDoorIds(originalDoorIds.filter(id => !removed.includes(id)));
          if (!doorIds.length) {
            setShowAddModal(false);
            await loadMasterCodes();
            setSubmitting(false);
            return;
          }
        } catch (error) {
          presentToastErrorContent(error);
          setSubmitting(false);
          return;
        }
      }
    }


    // Batch across chunks so a real progress bar can advance between requests
    // — a single call gives no feedback while many doors are being updated.
    if (doorsToProcess.length > BATCH_SIZE) {
      setProgress({ current: 0, total: doorsToProcess.length });
      let anyPartial = false;
      let anyPending = false;
      try {
        for (let i = 0; i < doorsToProcess.length; i += BATCH_SIZE) {
          const batch = doorsToProcess.slice(i, i + BATCH_SIZE);
          const res = await passcodeService.setMasterPasscode(
            formCode,
            formName,
            batch,
          );
          if (res.data?.partially) anyPartial = true;
          if (masterCodeResults(res.data).some(isPendingMasterCode))
            anyPending = true;
          setProgress({
            current: Math.min(i + batch.length, doorsToProcess.length),
            total: doorsToProcess.length,
          });
        }
        if (anyPartial) {
          presentToastWarning(t("passcodes.partiallyApplied"));
        } else if (anyPending) {
          presentToastWarning(t("accessList.schlage.pending"));
        } else {
          presentToastSuccess(
            isEditMode ? t("passcodes.updated") : t("passcodes.created"),
          );
        }
        setShowAddModal(false);
        await loadMasterCodes();
      } catch (err) {
        presentToastErrorContent(err);
      } finally {
        setProgress(null);
        setSubmitting(false);
      }
      return;
    }

    try {
      const res = await passcodeService.setMasterPasscode(
        formCode,
        formName,
        doorIds,
      );
      if (res.data?.partially) {
        presentToastWarning(res.data.message);
      } else if (masterCodeResults(res.data).some(isPendingMasterCode)) {
        presentToastWarning(t("accessList.schlage.pending"));
      } else {
        presentToastSuccess(
          res.data?.message ||
            (isEditMode ? t("passcodes.updated") : t("passcodes.created")),
        );
      }
      setShowAddModal(false);
      await loadMasterCodes();
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box gap="medium">
      {/* Header row */}
      <Box direction="row" justify="between" align="center" wrap>
        <Heading level="3" margin="none">
          {t("passcodes.masterCodesTitle")}
        </Heading>
        <Box direction="row" gap="small">
          <Button
            icon={<Refresh size="small" />}
            label={t("passcodes.sync")}
            onClick={handleSync}
            size="small"
          />
          <Button
            icon={<Add size="small" />}
            label={t("passcodes.addMasterCode")}
            primary
            onClick={handleOpenAdd}
            size="small"
          />
        </Box>
      </Box>

      {/* List */}
      {loading ? (
        <Box align="center" pad="medium">
          <Spinner />
        </Box>
      ) : masterCodes.length === 0 ? (
        <Text color="dark-4">{t("passcodes.noMasterCodes")}</Text>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
              <th style={{ padding: "8px" }}>{t("passcodes.colName")}</th>
              <th style={{ padding: "8px" }}>{t("passcodes.colCode")}</th>
              <th style={{ padding: "8px" }}>{t("passcodes.colDoors")}</th>
              <th style={{ padding: "8px" }} />
            </tr>
          </thead>
          <tbody>
            {masterCodes.map((master, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                }}
                onClick={() => handleOpenEdit(master)}
              >
                <td style={{ padding: "8px" }}>{master.name}</td>
                <td style={{ padding: "8px", fontFamily: "monospace" }}>
                  {master.code}
                </td>
                <td style={{ padding: "8px" }}>
                  {master.accesses ? (
                    <Box gap="xsmall">
                      {master.accesses.map((access) => (
                        <Box
                          key={access._id}
                          direction="row"
                          align="center"
                          gap="xsmall"
                        >
                          <Text>
                            {access.doorName} (
                            {access.lockType || hotel?.lockType})
                          </Text>
                          {isPendingMasterCode(access) && (
                            <Spinner
                              size="xsmall"
                              aria-label={t("accessList.schlage.pending")}
                            />
                          )}
                          <Text
                            size="small"
                            color={
                              access.status === "failed" || access.commandError
                                ? "status-critical"
                                : undefined
                            }
                          >
                            {t(
                              `accessList.schlage.${access.pendingOperation === "delete" ? "deleting" : access.commandError && access.status === "active" ? "deleteFailed" : access.status || "active"}`,
                            )}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    (master.doors || []).join(", ")
                  )}
                </td>
                <td
                  style={{ padding: "8px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box direction="row" gap="xsmall">
                    <Button
                      icon={<Edit size="small" />}
                      onClick={() => handleOpenEdit(master)}
                      plain
                      tip={t("passcodes.edit")}
                    />
                    <Button
                      icon={<Trash size="small" color="status-critical" />}
                      onClick={() => handleDelete(master)}
                      plain
                      tip={t("common.delete")}
                    />
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add / Edit modal */}
      {showAddModal && (
        <Layer
          onEsc={() => !submitting && setShowAddModal(false)}
          onClickOutside={() => !submitting && setShowAddModal(false)}
          position="center"
        >
          <Box pad="medium" gap="medium" width={{ min: "320px", max: "480px" }}>
            {progress ? (
              <>
                <Heading level="3" margin="none">
                  {isEditMode
                    ? t("passcodes.editMasterCode")
                    : t("passcodes.addMasterCode")}
                </Heading>
                <Box gap="xsmall">
                  <Meter
                    type="bar"
                    value={progress.current}
                    max={progress.total}
                    thickness="small"
                  />
                  <Text size="small" color="dark-4">
                    {t("passcodes.applyingProgress", {
                      current: progress.current,
                      total: progress.total,
                    })}
                  </Text>
                </Box>
              </>
            ) : (
              <>
                <Heading level="3" margin="none">
                  {isEditMode
                    ? t("passcodes.editMasterCode")
                    : t("passcodes.addMasterCode")}
                </Heading>

                <FormField label={t("passcodes.code")} required>
                  <TextInput
                    placeholder={t("passcodes.codePlaceholder")}
                    value={formCode}
                    onChange={(e) => setFormCode(e.target.value)}
                    type="text"
                    inputMode="numeric"
                    disabled={isEditMode}
                  />
                </FormField>

                <FormField label={t("passcodes.name")} required>
                  <TextInput
                    placeholder={t("passcodes.namePlaceholder")}
                    value={formName}
                    disabled={submitting}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                </FormField>

                <Box gap="xsmall">
                  <CheckBox
                    checked={allDoors}
                    disabled={submitting}
                    label={t("passcodes.allDoors")}
                    onChange={(e) => setAllDoors(e.target.checked)}
                  />
                  {!allDoors && eligibleDoors.length > 0 && (
                    <Box
                      border
                      pad="small"
                      gap="xsmall"
                      style={{ maxHeight: 200, overflowY: "auto" }}
                    >
                      {eligibleDoors.map((door) => (
                        <CheckBox
                          key={door._id}
                          checked={formDoorIds.includes(door._id)}
                          disabled={submitting}
                          label={door.name}
                          onChange={() => toggleDoor(door._id)}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                <Box direction="row" gap="small" justify="end">
                  <Button
                    label={t("common.cancel")}
                    onClick={() => setShowAddModal(false)}
                    disabled={submitting}
                  />
                  <Button
                    label={
                      submitting ? (
                        <Spinner size="xsmall" />
                      ) : isEditMode ? (
                        t("common.save")
                      ) : (
                        t("passcodes.create")
                      )
                    }
                    primary
                    icon={submitting ? undefined : <Checkmark size="small" />}
                    onClick={handleSave}
                    disabled={!formCode || !formName || submitting}
                  />
                </Box>
              </>
            )}
          </Box>
        </Layer>
      )}
    </Box>
  );
}
